import { useMemo } from 'react';
import type { Asset, AssetFolder, Project, ProjectAsset } from '@vidmakr/bindings/types';
import { mediaByProject, subtreeIds, totalBytes } from '@vidmakr/shared';
import { reducers } from './stdb';
import { attempt, toast } from './toast';
import { plural } from './selection';

/** Drag data for media (`a:<asset id>`) and media folders (`f:<folder id>`). */
export const MEDIA_DRAG = 'application/x-vidmakr-media';

/** A media library: 'all', 'library' (the shared one), 'space:<id>' or 'project:<id>'. */
export type LibraryId = string;

/** The library a media folder (or file) lives in. */
export const libraryOf = (x: { projectId: string; spaceId: string }): LibraryId => (x.projectId ? `project:${x.projectId}` : x.spaceId ? `space:${x.spaceId}` : 'library');

/** URL of a library (and folder) in the asset library page. */
export function assetsUrl(orgId: string, lib: LibraryId = 'all', folderId = '') {
  const q = new URLSearchParams();
  if (lib && lib !== 'all') q.set('lib', lib);
  if (folderId) q.set('folder', folderId);
  const s = q.toString();
  return `/orgs/${orgId}/assets${s ? `?${s}` : ''}`;
}

/**
 * Who uses which media, and sizes. A project's media are the files its clips
 * use (project_asset rows, which row-level security limits to projects the
 * viewer can open) plus its own project library; sizes count each file once
 * and only files the viewer can see.
 */
export function useMediaIndex(assets: readonly Asset[], projects: readonly Project[], usage: readonly ProjectAsset[]) {
  return useMemo(() => {
    const byId = new Map(assets.map((a) => [a.id, a]));
    const library = new Map<string, string[]>();
    for (const a of assets) if (a.projectId) library.set(a.projectId, [...(library.get(a.projectId) ?? []), a.id]);
    const used = mediaByProject(usage);
    const mediaOf = (p: Project) => [...new Set([...(used.get(p.id) ?? []), ...(library.get(p.id) ?? [])])];
    const usedBy = new Map<string, Project[]>();
    for (const p of projects) {
      for (const id of mediaOf(p)) usedBy.set(id, [...(usedBy.get(id) ?? []), p]);
    }
    /** Original file sizes of distinct media. */
    const sizeOf = (ids: Iterable<string>) => totalBytes(ids, (id) => byId.get(id)?.sizeBytes);
    /** Storage used, proxies and waveforms included. */
    const storageOf = (ids: Iterable<string>) => totalBytes(ids, (id) => byId.get(id)?.totalBytes);
    return { byId, mediaOf, sizeOf, storageOf, usedBy };
  }, [assets, projects, usage]);
}
export type MediaIndex = ReturnType<typeof useMediaIndex>;

/** Where a media move puts things: a folder (which decides the library), or the top of a library (undefined = where each item is). */
export interface MediaDest {
  folderId: string;
  projectId?: string;
  spaceId?: string;
}

/** Why media keys can't move to `dest`, or '' when they can. */
export function mediaMoveProblem(keys: string[], dest: MediaDest, folders: readonly AssetFolder[]): string {
  const byId = new Map(folders.map((f) => [f.id, f]));
  for (const k of keys) {
    if (!k.startsWith('f:')) continue;
    const f = byId.get(k.slice(2));
    if (!f) continue;
    if (dest.folderId && subtreeIds(folders, f.id).has(dest.folderId)) return 'A folder can’t move into itself';
    const projectId = dest.folderId ? byId.get(dest.folderId)?.projectId : dest.projectId;
    if (projectId !== undefined && projectId !== f.projectId) return 'Folders can’t move between a project’s files and other libraries';
  }
  return '';
}

/**
 * Moves media files and folders (selection keys). Items already there are
 * skipped; returns false when nothing moved or it failed.
 */
export async function moveMediaItems(keys: string[], dest: MediaDest, folders: readonly AssetFolder[], assets?: Map<string, Asset>): Promise<boolean> {
  const problem = mediaMoveProblem(keys, dest, folders);
  if (problem) return (toast.error(problem), false);
  const byId = new Map(folders.map((f) => [f.id, f]));
  const changesLibrary = dest.projectId !== undefined || dest.spaceId !== undefined;
  const destSpace = dest.folderId ? byId.get(dest.folderId)?.spaceId : dest.spaceId;
  const assetIds = keys
    .filter((k) => k.startsWith('a:'))
    .map((k) => k.slice(2))
    .filter((id) => {
      const a = assets?.get(id);
      return !assets || (!!a && (changesLibrary || a.folderId !== dest.folderId));
    });
  const folderMoves = keys
    .filter((k) => k.startsWith('f:'))
    .map((k) => byId.get(k.slice(2)))
    .filter((f): f is AssetFolder => !!f && f.id !== dest.folderId)
    .map((f) => ({ id: f.id, parentId: dest.folderId, spaceId: destSpace ?? f.spaceId, unchanged: f.parentId === dest.folderId && f.spaceId === (destSpace ?? f.spaceId) }))
    .filter((m) => !m.unchanged);
  const count = assetIds.length + folderMoves.length;
  if (!count) return false;
  const where = dest.folderId ? `“${byId.get(dest.folderId)?.name ?? 'folder'}”` : 'the top level';
  return attempt(async () => {
    if (assetIds.length) await reducers().moveAssets({ ids: assetIds, folderId: dest.folderId, projectId: dest.projectId, spaceId: dest.spaceId });
    for (const m of folderMoves) await reducers().moveFolder({ id: m.id, parentId: m.parentId, spaceId: m.spaceId });
  }, `Moved ${plural(count, 'item')} to ${where}`);
}
